import React, { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import classnames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PageType } from 'src/apollo/sites';
import { useTracker } from 'src/lib/js/hooks/useTracker';
import { useCart } from 'src/public/components/online_ordering/CartContext';
import { Channel } from 'src/public/js/siteUtilities';
import { useExperiment } from 'src/shared/components/common/ab_testing/ABTestContext';
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext';

import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from 'shared/components/common/modal';
import { usePopoverContext } from 'shared/components/common/popover/PopoverContext';

import { getCheckoutEligibleItems } from 'public/components/default_template/online_ordering/cart/cartUtils';

import Cart from './Cart';
import CartIcon from './CartIcon';

export const CART_POPOVER_CONTEXT_KEY = 'cartPopover';

const CartModal = ({ disabled }: {disabled?: boolean}) => {
  const { track } = useTracker();
  const { cart, channel } = useCart();
  const location = useLocation();
  const history = useHistory();
  const { ooRestaurant, orderingDisabled, currentPageType } = useRestaurant();
  const { ooReusableFlagGolf: ooCartSitesNavEnabled } = useFlags();
  const { selectedVariant } = useExperiment('woo-cart-in-navigation');
  const cartDisabled = channel !== Channel.ECOMM && orderingDisabled || !ooRestaurant || disabled;
  const numberOfItems = getCheckoutEligibleItems(cart)?.length || 0;
  const context = usePopoverContext(CART_POPOVER_CONTEXT_KEY);
  const isOrderPage = location.pathname.includes('/order');
  const isEcommPage = currentPageType === PageType.Ecommerce;

  const trackCartInNavigationClick = () => {
    track('cart_in_navigation_click', {
      isOrderPage,
      variant: selectedVariant,
      numberOfItems
    });
  };

  // When not on the order/ecomm page and no items, clicking the cart icon should take you to the order page or multi-location page
  // else it should open the modal
  const onModalClick = selectedVariant === 'test' && ooCartSitesNavEnabled && !isOrderPage && numberOfItems === 0 && !isEcommPage
    ? () => {
      trackCartInNavigationClick();
      history.push('/order');
    }
    : () => {
      !isEcommPage && trackCartInNavigationClick();
      context?.open();
    };

  useEffect(() => {
    // Freeze background behind cart when modal is open
    document.body.style.overflowY = context?.isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflowY = 'unset'; };
  }, [context?.isOpen]);

  if(!context) {
    return null;
  }

  return (
    <>
      <CartModalIconButton
        numberOfItems={numberOfItems}
        isModalOpen={context?.isOpen}
        onClick={onModalClick}
        cartDisabled={cartDisabled} />
      <Modal isOpen={context.isOpen} onClose={context.close} className="cartModal">
        <ModalOverlay fadeIn fadeOut />
        <ModalContent wrapperClassName="wrapper" contentClassName="content" slideIn slideOut ariaLabelledBy="cart-label">
          <div className="header">
            <div id="cart-label">
              Your Order
            </div>
            <ModalCloseButton className="closeButton" />
          </div>
          <Cart />
        </ModalContent>
      </Modal>
    </>
  );
};

export type CartModalIconButtonProps = {
  numberOfItems?: number;
  cartDisabled?: boolean;
  isModalOpen?: boolean;
  onClick?: () => void;
}

/**
 * A proxy for the CartModal when in the Editor so we dont have to provide the required Context Providers
 */
export const CartModalIconButton = ({ numberOfItems, cartDisabled, isModalOpen, onClick }: CartModalIconButtonProps) =>
  <button
    className="targetAction"
    aria-label={`Cart with ${numberOfItems} items`}
    aria-controls="cart-control"
    disabled={cartDisabled}
    aria-expanded={isModalOpen}
    onClick={onClick}>
    <div aria-hidden="true" className={classnames('cartPopoverTarget', { disabled: cartDisabled })}>
      <CartIcon />
      {numberOfItems}
    </div>
  </button>;


export default CartModal;
